// Session-persistence seam for Cognito auth tokens.
//
// Amplify v6 already ships browser storage adapters that conform to its
// KeyValueStorageInterface (async setItem / getItem / removeItem / clear), so we
// re-export them under our own names rather than re-implementing them. Feature
// code depends on THIS module, never on aws-amplify's import paths, which keeps
// the choice of token store in one place: swapping persistence for the whole app
// is a one-line change to DEFAULT_AUTH_STORAGE_KIND below.
//
// SECURITY: the default is the localStorage-backed store (persistent login,
// mirroring the mobile app). localStorage is readable by any script on the
// origin, so a successful XSS can exfiltrate the Cognito tokens. Two kinds narrow
// that exposure and are a one-line swap of the default kind: sessionStorage
// (dropped when the tab closes) and in-memory (dropped on reload, never
// persisted). It is left as localStorage here deliberately — persistent login is
// the product decision — and this seam makes hardening it a single edit.
//
// Cookie is NOT XSS hardening. Amplify's CookieStorage is client-side (js-cookie),
// so the token stays script-readable — it is not an HttpOnly cookie (only a
// server can set those). Choosing cookie changes persistence/transport, not XSS
// exposure, and makes the browser auto-attach the token to every same-path
// request (a CSRF consideration). If cookie is ever chosen, construct it with
// explicit hardening, e.g.
//   new CookieStorage({ secure: true, sameSite: 'strict', expires: <short days> })
// rather than relying on the unconfigured default below.

import {
  CookieStorage,
  defaultStorage,
  sessionStorage as amplifySessionStorage,
  sharedInMemoryStorage,
} from 'aws-amplify/utils';

// localStorage-backed. Survives tab close and browser restart.
export const localAuthStorage = defaultStorage;

// sessionStorage-backed. Cleared when the tab/window closes.
export const sessionAuthStorage = amplifySessionStorage;

// Map-based, process-lifetime only. Cleared on reload; never written to disk.
export const memoryAuthStorage = sharedInMemoryStorage;

// The cookie adapter is a class (per-instance config: path, sameSite, secure,
// expiry) rather than a shared singleton, so it is constructed lazily and
// memoised — a given kind must always return the same instance. Re-exported so
// feature code can build a configured cookie store without importing aws-amplify.
export { CookieStorage };

let cookieAuthStorage;
function getCookieAuthStorage() {
  if (!cookieAuthStorage) {
    // Unconfigured default (no secure / sameSite / expires) — a placeholder only.
    // Production cookie use must construct CookieStorage with explicit hardening
    // options; see the SECURITY note above.
    cookieAuthStorage = new CookieStorage();
  }
  return cookieAuthStorage;
}

// The single place the app's token persistence is chosen. Change this one value
// to swap every consumer: local <-> session <-> memory <-> cookie.
export const DEFAULT_AUTH_STORAGE_KIND = 'local';

const STORES = {
  local: () => localAuthStorage,
  session: () => sessionAuthStorage,
  memory: () => memoryAuthStorage,
  cookie: getCookieAuthStorage,
};

// Selector/factory returning the KeyValueStorageInterface store for `kind`. The
// same kind always yields the same instance; an unknown kind fails loudly rather
// than silently falling back, so a typo in a swap can't ship as "no persistence".
export function getAuthStorage(kind = DEFAULT_AUTH_STORAGE_KIND) {
  const resolve = STORES[kind];
  if (!resolve) {
    throw new Error(
      `Unknown auth storage kind "${kind}"; expected one of ${Object.keys(
        STORES,
      ).join(', ')}`,
    );
  }
  return resolve();
}
